import React from 'react';
import { Box, Flex, Button, Text } from '@indeed/ifl-components';
import type { SystemStyleObject } from '@indeed/ifl-css';
import { ChevronDown, ChevronUp } from '@indeed/ifl-icons';
import { SafeMarkup } from '@indeed/jspl-foundations';
import { AnswerPreview, type AnswerPreviewType } from '../AnswerPreview';
import useGettext from '../../util/GettextFactory';

export interface CollapsibleQuestionProps {
    /** Question prompt (HTML string; rendered via SafeMarkup in the collapsed preview). */
    questionLabel: string;
    /** Question type — drives the collapsed answer preview formatting. */
    type: AnswerPreviewType;
    /** Whether the question was already answered on load; controls whether it starts collapsed. */
    prefilled?: boolean;
    /**
     * Holds the question open and withdraws the collapse control. Set this while the answer is
     * blocking the form: folding it away would hide the field carrying the error, leaving the
     * question stuck in a failed state the jobseeker can neither see nor clear.
     */
    heldOpen?: boolean;
    /** Current answer value(s), shown in the collapsed preview. */
    values?: string[];
    /** Options for SELECT/MULTISELECT label resolution in the preview. */
    options?: { value: string; label: string }[];
    /** Locale used to format the collapsed date-answer preview. */
    locale?: string;
    /** The editable question field (renders its own label), shown when not prefilled or unfolded. */
    children: React.ReactNode;
    /** Style override applied to the outer container. */
    sx?: SystemStyleObject;
}

/**
 * Wraps a question so a prefilled answer shows as a compact, unfoldable preview,
 * while an unanswered (or unfolded) question shows the editable field.
 *
 * The toggle's accessible name is "Edit answer"/"Collapse answer" followed by the question prompt.
 * Match it accordingly in tests — and note that while expanded the prompt is in the DOM twice (the
 * field's own label plus a `hidden` copy naming the toggle), so `getByText` on it needs
 * `ignore: '[hidden], [hidden] *'`.
 */
export const CollapsibleQuestion: React.FunctionComponent<CollapsibleQuestionProps> = ({
    questionLabel,
    type,
    prefilled = false,
    heldOpen = false,
    values,
    options,
    locale,
    children,
    sx,
}) => {
    const gt = useGettext();
    const [unfolded, setUnfolded] = React.useState(false);
    const expanded = unfolded || heldOpen;
    const instanceId = React.useId();
    const fieldId = `collapsible-question-field-${instanceId}`;
    // The toggle names itself with its own text plus the question prompt, so a page of collapsed
    // questions doesn't read as a row of identical "Edit answer" buttons. `aria-labelledby` wins
    // over `aria-label`, and self-referencing the button folds its own label back in.
    const toggleId = `collapsible-question-toggle-${instanceId}`;
    const promptId = `collapsible-question-prompt-${instanceId}`;
    const toggleLabelledBy = `${toggleId} ${promptId}`;
    const editButtonRef = React.useRef<HTMLButtonElement>(null);
    const collapseButtonRef = React.useRef<HTMLButtonElement>(null);
    const hasToggledRef = React.useRef(false);

    // Collapsing/expanding swaps which chevron is mounted, so move focus to the control now
    // shown — otherwise keyboard/AT users are dropped to the page body when the button unmounts.
    React.useEffect(() => {
        if (!hasToggledRef.current) return;
        (expanded ? collapseButtonRef : editButtonRef).current?.focus();
    }, [expanded]);

    const toggle = (next: boolean) => {
        hasToggledRef.current = true;
        setUnfolded(next);
    };

    if (!prefilled) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, ...sx }}>
                <Flex
                    sx={{
                        alignSelf: 'flex-start',
                        alignItems: 'center',
                        backgroundColor: 'background.status.info.weaker',
                        borderRadius: 'sm',
                        paddingInline: 2,
                        paddingBlock: '3px',
                    }}
                >
                    <Text level={1} sx={{ fontWeight: 'medium', color: 'foreground.status.info' }}>
                        {gt.gettext('Answer question')}
                    </Text>
                </Flex>
                {children}
            </Box>
        );
    }

    if (expanded) {
        // The collapse control sits beside the field so the question label folds rather than
        // running under the button. Design:
        // https://www.figma.com/design/YqvUoqcp7hUVEjilAGM0ls/Auto-Apply-2026?node-id=19796-69984
        return (
            <Flex sx={{ alignItems: 'flex-start', gap: 2, ...sx }}>
                <Box id={fieldId} sx={{ flex: 1, minInlineSize: 0 }}>
                    {children}
                </Box>
                {/*
                 * Expanded, the prompt is drawn by the field itself and we don't own its id, so
                 * the button borrows this copy. `hidden` keeps it out of the reading order —
                 * otherwise the question would be announced twice — while still resolving for
                 * aria-labelledby, which reads referenced content regardless of visibility.
                 */}
                <span id={promptId} hidden>
                    <SafeMarkup level={3}>{questionLabel}</SafeMarkup>
                </span>
                {!heldOpen && (
                    <Button
                        ref={collapseButtonRef}
                        type="button"
                        variant="tertiary"
                        size="sm"
                        iconOnly
                        id={toggleId}
                        aria-label={gt.gettext('Collapse answer')}
                        aria-labelledby={toggleLabelledBy}
                        aria-expanded
                        aria-controls={fieldId}
                        onClick={() => toggle(false)}
                        sx={{ borderRadius: 'md' }}
                    >
                        <ChevronUp size="sm" aria-hidden />
                    </Button>
                )}
            </Flex>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ...sx }}>
            <Flex sx={{ alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                <Box id={promptId} sx={{ flex: 1, minInlineSize: 0 }}>
                    {/*
                     * Matches the jspl question label typography (level-3, normal). The top
                     * offset lines the collapsed question up with the jspl field's label (which
                     * sits slightly lower) so it doesn't shift on toggle.
                     */}
                    <SafeMarkup
                        level={3}
                        sx={{ color: 'foreground.strong', marginBlockStart: '3px' }}
                    >
                        {questionLabel}
                    </SafeMarkup>
                </Box>
                <Button
                    ref={editButtonRef}
                    type="button"
                    variant="tertiary"
                    size="sm"
                    iconOnly
                    id={toggleId}
                    aria-label={gt.gettext('Edit answer')}
                    aria-labelledby={toggleLabelledBy}
                    aria-expanded={false}
                    onClick={() => toggle(true)}
                    sx={{ borderRadius: 'md' }}
                >
                    <ChevronDown size="sm" aria-hidden />
                </Button>
            </Flex>
            <AnswerPreview
                type={type}
                values={values ?? []}
                options={options}
                level={3}
                locale={locale}
            />
        </Box>
    );
};
